import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import SettlementAccountModel, { SettlementAccountCreateType } from "../../../viewModels/Dictionaries/SettlementAccountModel";
import { SettlementAccountsFiltersType } from "../../../components/V2Components/SettlementAccountFilters";
import SettlementAccountHistoryModel from "../../../viewModels/Dictionaries/SettlementAccountHistoryModel";
import type { BankStatementImportPreviewModel, BankStatementImportRowModel } from "../../../viewModels/Payments/BankStatementImportModel";
import type { BankStatementImportHistoryDto } from "../../../api/apiServices/backOffice/controllers/bankStatementImports/bankStatementImports.dto";

const initialState: {
  loading: boolean;
  loadingData: boolean;
  error?: string;
  settlementAccounts?: Array<SettlementAccountModel>;
  page: number;
  pageSize: number;
  total?: number;
  filters?: SettlementAccountsFiltersType;

  settlementAccountIdToEdit?: number;
  isEditSettlementAccountDrawerLoading: boolean;
  isEditSettlementAccountDrawerOpen: boolean;

  historySettlementAccountId?: number;
  history?: Array<SettlementAccountHistoryModel>;
  historyTotal: number;
  historyPage: number;
  historyPageSize: number;
  historyModalOpen: boolean;
  historyLoading: boolean;
  historyLoadingData: boolean;

  bankStatementPreview?: BankStatementImportPreviewModel;
  bankStatementRows: Array<BankStatementImportRowModel>;
  bankStatementPreviewLoading: boolean;
  bankStatementConfirmLoading: boolean;
  bankStatementError?: string;
  bankStatementHistoryLoading: boolean;
  bankStatementHistoryError?: string;
  bankStatementHistory: Array<BankStatementImportHistoryDto>;
  bankStatementHistoryPage: number;
  bankStatementHistoryTotal: number;
} = {
  loading: true,
  loadingData: true,

  total: undefined!,
  pageSize: 10,
  page: 1,

  isEditSettlementAccountDrawerLoading: false,
  isEditSettlementAccountDrawerOpen: false,

  historyTotal: 0,
  historyPage: 1,
  historyPageSize: 10,
  historyModalOpen: false,
  historyLoading: false,
  historyLoadingData: false,

  bankStatementRows: [],
  bankStatementPreviewLoading: false,
  bankStatementConfirmLoading: false,
  bankStatementHistoryLoading: false,
  bankStatementHistory: [],
  bankStatementHistoryPage: 0,
  bankStatementHistoryTotal: 0,
};

const settlementAccountsSlice = createSlice({
  name: "settlementAccounts",
  initialState,
  reducers: {
    loadSettlementAccounts(state) {
      state.loadingData = true;
      state.error = undefined;
      state.page = 1;
    },
    loadSettlementAccountsSuccess(state, action: PayloadAction<{ settlementAccounts?: Array<SettlementAccountModel>; total: number }>) {
      const { settlementAccounts, total } = action.payload;

      state.loading = false;
      state.loadingData = false;
      state.settlementAccounts = settlementAccounts;
      state.total = total;
      state.error = undefined;
    },
    loadSettlementAccountsReject(state, action: PayloadAction<string>) {
      state.loading = false;
      state.loadingData = false;
      state.error = action.payload;
    },
    changeTable(state, action: PayloadAction<{ page: number; pageSize: number; filters: SettlementAccountsFiltersType }>) {
      const { page, pageSize, filters } = action.payload;
      state.loadingData = true;
      state.page = page;
      state.pageSize = pageSize;
      state.filters = filters;
    },
    openEditSettlementAccountDrawer(state, action: PayloadAction<number | undefined>) {
      state.settlementAccountIdToEdit = action.payload;
      state.isEditSettlementAccountDrawerOpen = true;
    },
    closeEditSettlementAccountDrawer(state) {
      if (!state.isEditSettlementAccountDrawerLoading) {
        state.settlementAccountIdToEdit = undefined;
        state.isEditSettlementAccountDrawerOpen = false;
      }
    },

    createSettlementAccount(state, _action: PayloadAction<SettlementAccountCreateType>) {
      state.isEditSettlementAccountDrawerLoading = true;
      state.error = undefined;
    },
    createSettlementAccountSuccess(state) {
      state.isEditSettlementAccountDrawerLoading = false;
      state.isEditSettlementAccountDrawerOpen = false;
    },

    updateSettlementAccount(state, _action: PayloadAction<SettlementAccountModel>) {
      state.isEditSettlementAccountDrawerLoading = true;
      state.error = undefined;
    },
    saveSettlementAccountReject(state, action: PayloadAction<string>) {
      state.isEditSettlementAccountDrawerLoading = false;
      state.error = action.payload;
    },
    updateSettlementAccountSuccess(state, action: PayloadAction<SettlementAccountModel>) {
      state.isEditSettlementAccountDrawerLoading = false;
      state.isEditSettlementAccountDrawerOpen = false;
      if (state.settlementAccounts) {
        const index = state.settlementAccounts.findIndex((x) => x.id === action.payload.id);
        state.settlementAccounts[index] = action.payload;
      }
    },

    deleteSettlementAccount(state, _action: PayloadAction<number>) {
      state.loadingData = true;
    },
    openHistoryModal(state, action: PayloadAction<number>) {
      state.historySettlementAccountId = action.payload;
      state.history = undefined;
      state.historyPage = 1;
      state.historyModalOpen = true;
      state.historyLoading = true;
      state.historyLoadingData = true;
    },
    closeHistoryModal(state) {
      state.historyModalOpen = false;
      state.historySettlementAccountId = undefined;
      state.history = undefined;
      state.historyTotal = 0;
      state.historyLoading = false;
      state.historyLoadingData = false;
    },
    changeHistoryPage(state, action: PayloadAction<number>) {
      state.historyPage = action.payload;
      state.historyLoadingData = true;
    },
    loadHistorySuccess(state, action: PayloadAction<{ history: Array<SettlementAccountHistoryModel>; total: number }>) {
      state.history = action.payload.history;
      state.historyTotal = action.payload.total;
      state.historyLoading = false;
      state.historyLoadingData = false;
    },
    loadHistoryFailure(state) {
      state.historyLoading = false;
      state.historyLoadingData = false;
    },
    previewBankStatement(state, _action: PayloadAction<File>) {
      state.bankStatementPreviewLoading = true;
      state.bankStatementError = undefined;
    },
    previewBankStatementSuccess(state, action: PayloadAction<BankStatementImportPreviewModel>) {
      state.bankStatementPreviewLoading = false;
      state.bankStatementPreview = action.payload;
      state.bankStatementRows = action.payload.rows;
    },
    previewBankStatementReject(state, action: PayloadAction<string | undefined>) {
      state.bankStatementPreviewLoading = false;
      state.bankStatementError = action.payload;
    },
    openBankStatementHistory(state) {
      state.bankStatementHistoryPage = 0;
      state.bankStatementHistoryLoading = true;
      state.bankStatementHistoryError = undefined;
      state.bankStatementHistory = [];
      state.bankStatementHistoryTotal = 0;
    },
    changeBankStatementHistoryPage(state, action: PayloadAction<number>) {
      state.bankStatementHistoryPage = action.payload;
      state.bankStatementHistoryLoading = true;
      state.bankStatementHistoryError = undefined;
    },
    loadBankStatementHistorySuccess(state, action: PayloadAction<{ items: Array<BankStatementImportHistoryDto>; total: number }>) {
      state.bankStatementHistory = action.payload.items;
      state.bankStatementHistoryTotal = action.payload.total;
      state.bankStatementHistoryLoading = false;
      state.bankStatementHistoryError = undefined;
    },
    loadBankStatementHistoryReject(state, action: PayloadAction<string | undefined>) {
      state.bankStatementHistoryLoading = false;
      state.bankStatementHistoryError = action.payload ?? "Не удалось загрузить историю выписок";
      state.bankStatementHistory = [];
      state.bankStatementHistoryTotal = 0;
    },
    loadBankStatementImport(state, _action: PayloadAction<number>) {
      state.bankStatementPreviewLoading = true;
      state.bankStatementError = undefined;
    },
    rediscoverBankStatement(state) {
      state.bankStatementPreviewLoading = true;
      state.bankStatementError = undefined;
    },
    updateBankStatementRows(state, action: PayloadAction<Array<BankStatementImportRowModel>>) {
      state.bankStatementRows = action.payload;
    },
    closeBankStatementPreview(state) {
      if (!state.bankStatementConfirmLoading && !state.bankStatementPreviewLoading) {
        state.bankStatementPreview = undefined;
        state.bankStatementRows = [];
        state.bankStatementError = undefined;
      }
    },
    confirmBankStatement(state) {
      state.bankStatementConfirmLoading = true;
      state.bankStatementError = undefined;
    },
    confirmBankStatementSuccess(state) {
      state.bankStatementConfirmLoading = false;
      state.bankStatementPreview = undefined;
      state.bankStatementRows = [];
      state.bankStatementError = undefined;
    },
    confirmBankStatementReject(state, action: PayloadAction<string | undefined>) {
      state.bankStatementConfirmLoading = false;
      state.bankStatementError = action.payload;
    },
  },
});

export default settlementAccountsSlice;
